import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Autocomplete, PrimaryButton, SecondaryButton, Typography, Checkbox } from '@weareopenr/component-library';
import { IPublishFormValues, PublishType, Steps } from '../../types';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import styles from './ExcludeProducts.module.scss';
import { useProductLibraryItems } from 'src/hooks/useProductLibraryItems';
import { ProductSizeItem } from 'src/api/types';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { AutocompleteOptionWithRanges } from 'src/components/AutocompleteOptionWithRanges';
import { capitalise } from 'src/helpers/capitalise';

export const ExcludeProducts = ({
  defaultValues,
  submitDataToParent,
}: {
  defaultValues: IPublishFormValues;
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: number) => void;
}) => {
  const defaultExcludedItems = defaultValues.excludedItems ?? [];

  const [searchValue, setSearchValue] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<ProductSizeItem[]>(defaultExcludedItems);
  const [checkedItemIds, setCheckedItemIds] = useState<Set<string>>(
    new Set(defaultExcludedItems.map((p) => p.item_id).filter(Boolean)),
  );

  // Set of selected item_ids (excluded items) for O(1) lookup
  const selectedItemIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.item_id).filter(Boolean)),
    [selectedProducts],
  );

  // Only checked selected products
  const excludedItems = useMemo(
    () => selectedProducts.filter(({ item_id }) => !!item_id && checkedItemIds.has(item_id)),
    [selectedProducts, checkedItemIds],
  );

  const debouncedSearchValue = useDebounce(searchValue);
  // keepPreviousData avoids a dropdown blink to empty while a new search term refetches.
  const { data, fetchNextPage, hasNextPage } = useProductLibraryItems({
    searchQuery: debouncedSearchValue,
    options: { keepPreviousData: true },
  });

  const { handleSubmit } = useForm<Pick<IPublishFormValues, 'excludedItems'>>({
    defaultValues: {
      excludedItems: defaultExcludedItems,
    },
  });

  const loadMoreProducts = () => {
    if (hasNextPage) fetchNextPage();
  };

  const selectProduct = useCallback(
    (product: ProductSizeItem) => {
      if (!product.item_id || selectedItemIds.has(product.item_id)) return;

      setSelectedProducts((prev) => [...prev, product]);

      setCheckedItemIds((prev) => {
        const next = new Set(prev);
        next.add(product.item_id);
        return next;
      });
    },
    [selectedItemIds],
  );

  const toggleProductCheckbox = useCallback((product: ProductSizeItem) => {
    if (!product.item_id) return;

    setCheckedItemIds((prev) => {
      const checkedIds = new Set(prev);
      if (checkedIds.has(product.item_id)) {
        checkedIds.delete(product.item_id);
      } else {
        checkedIds.add(product.item_id);
      }
      return checkedIds;
    });
  }, []);

  const allItems = data?.pages.flatMap((page) => page.data) || [];
  const availableItems = allItems.flatMap((item) => {
    // If item has an item_id, it's a standalone product
    if (item.item_id) {
      return selectedItemIds.has(item.item_id) ? [] : [item];
    }

    // If item has no item_id but has product_sizes, flatten them
    if (item.product_sizes && item.product_sizes.length > 0) {
      return item.product_sizes
        .filter((size) => size.item_id && !selectedItemIds.has(size.item_id))
        .map(
          (size): ProductSizeItem => ({
            ...size,
            product_id: null,
            product_sizes: [],
          }),
        );
    }
    // Otherwise, skip this item
    return [];
  });

  const onSubmit = () => {
    submitDataToParent({ excludedItems });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['container']}>
      <ModalTitle title="Exclude Products" subTitle="Search for products that you'd like to exclude from publication" />
      <Autocomplete
        name="exclude-products-search"
        label="Search Products"
        filter="none"
        options={availableItems.flatMap((item) => {
          if (!item.item_id) return [];
          const itemLabel = item.type ? `${item.name} (${capitalise(item.type)})` : item.name;
          return [
            {
              value: item.item_id,
              label: itemLabel,
              content: <AutocompleteOptionWithRanges option={{ ...item, name: itemLabel }} />,
            },
          ];
        })}
        value={undefined}
        onChange={(itemId) => {
          const product = availableItems.find((item) => item.item_id === itemId);
          if (product) selectProduct(product);
        }}
        onSearchChange={setSearchValue}
        onLoadMore={loadMoreProducts}
      />
      <div className={styles['excluded-section']}>
        {selectedProducts.length === 0 && (
          <Typography className={styles['no-selected']}>No products selected</Typography>
        )}
        {selectedProducts.length > 0 && (
          <div className={styles['excluded-list']}>
            {selectedProducts.map((product, index) => (
              <div key={`${product.item_id}-${index}`} className={styles['excluded-item']}>
                <Checkbox
                  label=""
                  name={`excluded-${product.item_id}`}
                  checked={checkedItemIds.has(product.item_id)}
                  onChange={() => toggleProductCheckbox(product)}
                  labelWeight="regular"
                >
                  <Typography size="medium">
                    {product.type ? `${product.name} (${capitalise(product.type)})` : product.name}
                  </Typography>
                </Checkbox>
              </div>
            ))}
          </div>
        )}
      </div>

      <ButtonContainer
        left={
          <SecondaryButton
            onClick={() => {
              const previousStep = defaultValues.type === PublishType.PRODUCT ? Steps.instance : Steps.venues;
              submitDataToParent({ excludedItems }, previousStep);
            }}
            size="full-width"
            buttonStyle="outline"
          >
            Back
          </SecondaryButton>
        }
        right={
          <PrimaryButton type="submit" size="full-width">
            Continue
          </PrimaryButton>
        }
      />
    </form>
  );
};
